import type { components } from '../../api/generated/api-types';

export type ScheduleUnit = 'minutes' | 'hours' | 'days';
export type SchedulePolicy = components['schemas']['ConnectorCapabilities']['scheduled_sync'];

export interface SyncScheduleValue {
  enabled: boolean;
  amount: number;
  unit: ScheduleUnit;
}

const MINUTES_PER_UNIT: Record<ScheduleUnit, number> = { minutes: 1, hours: 60, days: 1440 };

export function scheduleToMinutes(value: SyncScheduleValue): number | null {
  if (!Number.isInteger(value.amount) || value.amount <= 0) return null;
  const minutes = value.amount * MINUTES_PER_UNIT[value.unit];
  return Number.isSafeInteger(minutes) ? minutes : null;
}

export function validateSchedule(value: SyncScheduleValue, policy?: SchedulePolicy | null): string | null {
  if (!value.enabled) return null;
  if (!policy) return 'Scheduled synchronization is not supported by this connector.';
  const minutes = scheduleToMinutes(value);
  if (minutes === null) return 'Enter a positive whole number.';
  if (minutes < policy.minimum_minutes || minutes > policy.maximum_minutes) {
    return `The interval must be between ${policy.minimum_minutes} and ${policy.maximum_minutes} minutes.`;
  }
  return null;
}

export function initialSyncSchedule(policy?: SchedulePolicy | null, interval?: number | null): SyncScheduleValue {
  const minutes = interval ?? policy?.default_minutes ?? 60;
  const [amount, unit]: [number, ScheduleUnit] = minutes % 1440 === 0
    ? [minutes / 1440, 'days']
    : minutes % 60 === 0
      ? [minutes / 60, 'hours']
      : [minutes, 'minutes'];
  return { enabled: interval != null, amount, unit };
}

export function supportsScheduling(policy?: SchedulePolicy | null): boolean {
  return policy != null;
}